import { inject, Inject, Injectable, makeStateKey, PLATFORM_ID, TransferState } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { SSR_URL } from '@finder/shared/constants/app.constants';

/**
 * Return the global native browser window object
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
const _window = (isServer, location): any => {
    try {
        if (isServer) {
            const storageStub = {
                setItem: () => {},
                getItem: () => void 0,
                removeItem: () => {},
            };

            // @SSR: Returning stub for SSR
            return {
                location: new URL(location),
                sessionStorage: storageStub,
                localStorage: storageStub,
                navigator: {
                    userAgent: '',
                    clipboard: {
                        writeText: () => {}
                    }
                },
                innerWidth: 1024, // mark the server as desktop
                innerHeight: 0,
                open: () => {},
                matchMedia: () => {},
                getComputedStyle: () => ({
                    getPropertyValue: () => {}
                }),
                IntersectionObserver: class Observer {
                    constructor(arg1) {}
                    observe() {}
                    unobserve() {}
                    disconnect() {}
                },
                addEventListener: () => {},
                removeEventListener: () => {},
                requestAnimationFrame: () => {},
                scroll: () => {},
                history: {
                    replaceState: () => {}
                },
                document: {
                    getElementById: () => {},
                    addEventListener: () => {},
                    removeEventListener: () => {},
                    body: {
                        classList: [],
                        style: {
                            setProperty: () => {}
                        }
                    }
                }
            };
        }

        return window;
    } catch (err) { }
};

/**
 * Export a reference of the global native browser window object.
 */
@Injectable({
    providedIn: 'root',
})
export class WindowRef {
    private renderingOnTheServer = false;
    private injectedLocation = inject(SSR_URL, { optional: true });
    private dataKey = makeStateKey<boolean>('renderedOnServer');

    // Don't pull in ConfigService. It causes a circular dependency loop
    constructor(
        private tranferState: TransferState,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        this.renderingOnTheServer = isPlatformServer(this.platformId);

        // If the data is rendered on the server then update the transfer state
        if (this.renderingOnTheServer) {
            this.tranferState.set(this.dataKey, true);
        }
    }

    get nativeWindow(): any {
        return _window(this.renderingOnTheServer, this.injectedLocation);
    }
}
